import { useCallback, useEffect, useMemo, useState } from 'react';

// APIs
import * as DataListAPI from '../../../../api/dataList';
import * as ProdutoAPI from '../../../../api/produto';

export function useMateriaPrimaModule({
    dados,
    setDados, // não é obrigatório aqui, mas mantém padrão
    parametro,
    // MRT state (centraliza aqui)
    mrt,
    setFlashMessage,
    pickSelectedId,
}) {
    // extrai MRT state (evita undefined)
    const pagination = mrt?.pagination ?? { pageIndex: 0, pageSize: 5 };
    const columnFilters = mrt?.columnFilters ?? [];
    const columnFilterFns = mrt?.columnFilterFns ?? [];
    const globalFilter = mrt?.globalFilter ?? '';
    const sorting = mrt?.sorting ?? [];

    // -------------------------
    // helpers
    // -------------------------
    const consultaDatalist = useCallback(
        async (params) => {
            return await DataListAPI.dataList(params);
        },
        [DataListAPI]
    );

    const getIdByValueDom = useCallback((value) => {
        return document.querySelector(`datalist option[value="${value}"]`)?.dataset.id || null;
    }, []);

    const pickId = useCallback(
        (value, result, field = 'descricao') => {
            if (typeof pickSelectedId === 'function') return pickSelectedId(value, result, field);
            const found = (result || []).find((x) => String(x?.[field] || '') === String(value || ''));
            return found?.id ?? null;
        },
        [pickSelectedId]
    );

    // -------------------------
    // state: datalist materiaPrima
    // -------------------------
    const [materiaPrima, setMateriaPrima] = useState({});
    const [OptionsMateriaPrima, setOptionsMateriaPrima] = useState([]);

    // -------------------------
    // state: grid
    // -------------------------
    const [produtoComposicoes, setProdutoComposicoes] = useState([]);
    const [rowCountComposicoes, setRowCountComposicoes] = useState(0);

    const baseNovaMateriaPrima = useMemo(
        () => ({
            uuid: null,
            empresaId: dados?.empresaId ?? null,
            produtoId: dados?.id ?? null,

            materiaPrimaId: null,
            materiaPrima: {},

            percentual: null,

            ingrediente: false,
            recheio: false,
            alteraEstoque: false,
            calculaUnidade: false,

            ativo: true,
            funcao: 'Adicionar',
            index: null,
            id: null,
        }),
        [dados?.empresaId, dados?.id]
    );

    const [novaMateriaPrima, setNovaMateriaPrima] = useState(baseNovaMateriaPrima);

    useEffect(() => {
        // quando troca produto/empresa, reseta draft
        setNovaMateriaPrima(baseNovaMateriaPrima);
        setMateriaPrima({});
    }, [baseNovaMateriaPrima]);

    // -------------------------
    // actions: datalist materia prima
    // -------------------------
    const changeMateriaPrimaDescricao = useCallback(
        async (value) => {
            setMateriaPrima((prev) => ({ ...prev, descricao: value }));

            const result = await consultaDatalist({
                empresaId: dados?.empresaId,
                tabela: 'Produto',
                condicao: 'materiaPrima',
                filtro: value,
            });

            setOptionsMateriaPrima(result || []);

            const selectedId = getIdByValueDom(value) || pickId(value, result, 'descricao');
            const selected = (result || []).find((x) => String(x?.id) === String(selectedId));

            setNovaMateriaPrima((prev) => ({
                ...prev,
                materiaPrimaId: selectedId ?? null,
                materiaPrima: selected ?? prev.materiaPrima,
            }));

            if (selected) setMateriaPrima(selected);
            return selectedId;
        },
        [consultaDatalist, dados?.empresaId, getIdByValueDom, pickId]
    );

    // -------------------------
    // actions: draft
    // -------------------------
    const limparMateriaPrima = useCallback(() => {
        setNovaMateriaPrima(baseNovaMateriaPrima);
        setMateriaPrima({});
    }, [baseNovaMateriaPrima]);

    const selectMateriaPrimaRow = useCallback(
        (originalRow, rowIndex, funcao) => {
            const newData = {
                uuid: originalRow.uuid,
                id: originalRow.id,

                materiaPrima: originalRow.materiaPrima || originalRow.materiaPrimaProduto || {},

                empresaId: originalRow.empresaId || dados?.empresaId,
                produtoId: originalRow.produtoId || dados?.id,
                materiaPrimaId: originalRow.materiaPrimaId,
                percentual: originalRow.percentual,

                ingrediente: originalRow.ingrediente,
                recheio: !!originalRow.recheio,
                alteraEstoque: !!originalRow.alteraEstoque,
                calculaUnidade: !!originalRow.calculaUnidade,

                ativo: !!originalRow.ativo,
                funcao,
                index: rowIndex,
            };

            setNovaMateriaPrima(newData);
            setMateriaPrima(newData.materiaPrima || {});
        },
        [dados?.empresaId, dados?.id]
    );

    // -------------------------
    // remote fetch
    // -------------------------
    const refresh = useCallback(
        async ({ start = 0, size = 50, filtros, filtrosMode, globalFilter, sorting } = {}) => {
            if (!dados?.id) {
                setRowCountComposicoes(produtoComposicoes?.length || 0);
                return;
            }

            try {
                // ajuste conforme sua API real
                const resp = await ProdutoAPI.listarMateriaPrima({
                    start: pagination.pageIndex * pagination.pageSize,
                    size: pagination.pageSize,
                    filters: JSON.stringify(columnFilters ?? []),
                    filtersMode: JSON.stringify(columnFilterFns ?? []),
                    globalFilter: globalFilter ?? '',
                    sorting: JSON.stringify(sorting ?? []),
                    produtoId: dados.id ?? null,
                });

                const data = resp?.data?.data ?? resp?.data ?? [];
                const total = resp?.data?.meta?.totalRowCount ?? data.length ?? 0;

                setProdutoComposicoes(Array.isArray(data) ? data : []);
                setRowCountComposicoes(total);
            } catch (e) {
                console.error(e);
                setFlashMessage?.('Erro ao carregar composição.', 'error');
            }
        },
        [ProdutoAPI, dados?.id, produtoComposicoes?.length, setFlashMessage]
    );

    // -------------------------
    // save
    // -------------------------
    const saveMateriaPrima = useCallback(async () => {
        const payload = {
            ...novaMateriaPrima,
            empresaId: novaMateriaPrima.empresaId ?? dados?.empresaId,
            produtoId: novaMateriaPrima.produtoId ?? dados?.id,
            materiaPrima: novaMateriaPrima.materiaPrima ?? materiaPrima,
        };

        // validação mínima
        if (!payload?.materiaPrimaId || !payload?.percentual) return;

        if (dados?.id) {
            try {
                const result = await ProdutoAPI.createOrEditMateriaPrima(payload);
                setFlashMessage?.(result?.message || 'Ok', result?.type || 'success');
                if (result?.type === 'success') {
                    await refresh({ start: 0, size: 50 });
                }
            } catch (e) {
                console.error(e);
                setFlashMessage?.('Erro ao salvar matéria-prima.', 'error');
            } finally {
                limparMateriaPrima();
            }
            return;
        }

        // sem produto salvo: local
        setProdutoComposicoes((prev) => {
            const list = Array.isArray(prev) ? [...prev] : [];
            const f = String(novaMateriaPrima?.funcao || '').toLowerCase();

            if (f === 'editar') {
                return list.map((it, i) => (i === novaMateriaPrima.index ? { ...it, ...payload } : it));
            }
            if (f === 'deletar') {
                return list.filter((it) => it.id !== novaMateriaPrima.id && it.uuid !== novaMateriaPrima.uuid);
            }
            return [...list, payload];
        });

        limparMateriaPrima();
    }, [ProdutoAPI, dados?.empresaId, dados?.id, refresh, limparMateriaPrima, materiaPrima, novaMateriaPrima, setFlashMessage]);

    // -------------------------
    // change handler único
    // -------------------------
    const change = useCallback(
        async (e) => {
            const { name, value, type, checked } = e.target;

            if (name === 'novaMateriaPrima.materiaPrima.descricao') {
                await changeMateriaPrimaDescricao(value);
                return;
            }

            if (name === 'novaMateriaPrima.percentual') {
                setNovaMateriaPrima((prev) => ({ ...prev, percentual: value }));
                return;
            }

            if (name === 'novaMateriaPrima.ativo') {
                setNovaMateriaPrima((prev) => ({ ...prev, ativo: !!checked }));
                return;
            }

            // flags padaria (ou geral)
            if (name.startsWith('novaMateriaPrima.')) {
                const key = name.split('.')[1];
                setNovaMateriaPrima((prev) => ({ ...prev, [key]: type === 'checkbox' ? !!checked : value }));
                return;
            }
        },
        [changeMateriaPrimaDescricao]
    );

    return {
        state: {
            parametro,
            materiaPrima,
            OptionsMateriaPrima,

            novaMateriaPrima,
            produtoComposicoes,
            rowCountComposicoes,
        },
        actions: {
            change,
            limparMateriaPrima,
            saveMateriaPrima,
            selectMateriaPrimaRow,
            refresh,
        },
    };
}
